/**
 * Position Store - SSE-First Position State Management
 * 
 * This store implements the SSE-first architecture for position state management.
 * It replaces REST API calls with direct SSE event handling for real-time position updates.
 * 
 * SECURITY: This store uses user-aware persistence to prevent cross-user data contamination.
 * The localStorage key is dynamically generated based on the current user context.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware';
import { LiveTrade } from '../services/api';
import {
  PositionSnapshotEvent,
  PositionOpenedEvent,
  PositionClosedEvent,
  PositionPnlChangeEvent,
  PositionModifiedEvent,
  PositionMilestoneEvent,
  PositionsRefreshEvent,
} from '../types/sse-events';

interface PositionState {
  // Position data
  positions: Map<string, LiveTrade>;
  lastUpdate: number;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Metadata
  totalPositions: number;
  totalUnrealizedPnl: number;
  lastSnapshotTime: number | null;
  
  // Connection state
  isConnected: boolean;
  connectionError: string | null;
  snapshotRequested: boolean;
  
  // SECURITY: User context for data isolation
  currentUserId: string | null;
  setUserContext: (userId: string | null) => void;
  clearUserData: () => void;
  
  // SSE Event Handlers
  handlePositionSnapshot: (event: PositionSnapshotEvent) => void;
  handlePositionOpened: (event: PositionOpenedEvent) => void;
  handlePositionClosed: (event: PositionClosedEvent) => void;
  handlePositionPnlChange: (event: PositionPnlChangeEvent) => void;
  handlePositionModified: (event: PositionModifiedEvent) => void;
  handlePositionMilestone: (event: PositionMilestoneEvent) => void;
  handlePositionsRefresh: (event: PositionsRefreshEvent) => void;
  
  // State management
  requestPositionSnapshot: () => void;
  clearPositions: () => void;
  resetError: () => void;
  setLoading: (loading: boolean) => void;
  setConnectionStatus: (connected: boolean, error?: string) => void;
  
  // 🚀 NEW: Cache hydration
  initializeFromCache: (cachedData: LiveTrade[]) => void;
  
  // Selectors
  getPosition: (account: string, symbol: string) => LiveTrade | undefined;
  getPositionsByAccount: (account: string) => LiveTrade[];
  hasOpenPositions: () => boolean;
  getPositionsBySymbol: (symbol: string) => LiveTrade[];
  getTotalPnl: () => number;
}

/**
 * Generate unique position key for Map storage
 */
const getPositionKey = (account: string, symbol: string): string => {
  return `${account}:${symbol}`;
};

/**
 * Check if two positions have identical data (for smart merging)
 */
const positionsAreEqual = (pos1: LiveTrade, pos2: LiveTrade): boolean => {
  return (
    pos1.symbol === pos2.symbol &&
    pos1.side === pos2.side &&
    pos1.accountname === pos2.accountname &&
    pos1.unrealisedPnl === pos2.unrealisedPnl &&
    pos1.avgPrice === pos2.avgPrice &&
    pos1.markPrice === pos2.markPrice &&
    pos1.takeProfit === pos2.takeProfit &&
    pos1.stopLoss === pos2.stopLoss &&
    pos1.size === pos2.size &&
    pos1.positionValue === pos2.positionValue &&
    pos1.leverage === pos2.leverage &&
    pos1.strategyname === pos2.strategyname
    // Note: We're not comparing seq and createdTime as they might differ between REST/SSE
  );
};

/**
 * Calculate total unrealized PnL from all positions
 */
const calculateTotalPnl = (positions: Map<string, LiveTrade>): number => {
  let total = 0;
  for (const position of positions.values()) {
    const pnl = parseFloat(position.unrealisedPnl);
    if (!isNaN(pnl)) {
      total += pnl;
    }
  }
  return total;
};

/**
 * SECURITY: Generate user-aware localStorage key for position store
 */
export const getPositionStoreKey = (userId: string | null): string => {
  if (!userId) {
    return 'position-store-persist-anonymous';
  }
  return `position-store-persist-${userId}`;
};

/**
 * SECURITY: Clear all position store keys from localStorage (for cleanup)
 */
export const clearAllPositionStoreKeys = (): void => {
  const keys = Object.keys(localStorage);
  const positionStoreKeys = keys.filter(key => key.startsWith('position-store-persist'));
  
  positionStoreKeys.forEach(key => {
    console.log(`🧹 [Position Store] Removing localStorage key: ${key}`);
    localStorage.removeItem(key);
  });
  
  console.log(`✅ [Position Store] Cleared ${positionStoreKeys.length} position store keys`);
};

/**
 * SECURITY: Clear legacy position store key (for migration)
 */
export const clearLegacyPositionStoreKey = (): void => {
  const legacyKey = 'position-store-persist';
  if (localStorage.getItem(legacyKey)) {
    console.log('🧹 [Position Store] Removing legacy localStorage key:', legacyKey);
    localStorage.removeItem(legacyKey);
  }
};

/**
 * SECURITY: User-aware storage implementation
 * This prevents Zustand persist middleware from loading wrong user's data
 */
let currentStorageUserId: string | null = null;

const userAwareStorage: StateStorage = {
  getItem: (_name: string) => {
    // Generate key based on current user context
    const key = currentStorageUserId ? getPositionStoreKey(currentStorageUserId) : 'position-store-persist-anonymous';
    const value = localStorage.getItem(key);
    
    if (value) {
      console.log(`📖 [Position Store Storage] Reading from key: ${key.substring(0, 50)}...`);
    }
    
    return value;
  },
  
  setItem: (_name: string, value: string) => {
    // Generate key based on current user context
    const key = currentStorageUserId ? getPositionStoreKey(currentStorageUserId) : 'position-store-persist-anonymous';
    console.log(`💾 [Position Store Storage] Writing to key: ${key.substring(0, 50)}...`);
    localStorage.setItem(key, value);
  },
  
  removeItem: (_name: string) => {
    const key = currentStorageUserId ? getPositionStoreKey(currentStorageUserId) : 'position-store-persist-anonymous';
    console.log(`🗑️ [Position Store Storage] Removing key: ${key.substring(0, 50)}...`);
    localStorage.removeItem(key);
  }
};

/**
 * SECURITY: Update storage user context - must be called before any persist operations
 */
export const setPositionStoreUserId = (userId: string | null): void => {
  const userLabel = userId ? `${userId.substring(0, 20)}...` : 'anonymous';
  console.log(`🔐 [Position Store Storage] User context: ${userLabel}`);
  currentStorageUserId = userId;
};

/**
 * Update metadata based on current positions
 */
const updateMetadata = (state: PositionState) => {
  state.totalPositions = state.positions.size;
  state.totalUnrealizedPnl = calculateTotalPnl(state.positions);
  state.lastUpdate = Date.now();
};

export const usePositionStore = create<PositionState>()(  
  persist(
    devtools(
      immer((set, get) => ({
        // Initial state
        positions: new Map<string, LiveTrade>(),
        lastUpdate: 0,
        isInitialized: false,
        isLoading: false,
        error: null,
        
        // Metadata
        totalPositions: 0,
        totalUnrealizedPnl: 0,
        lastSnapshotTime: null,
        
        // Connection state
        isConnected: false,
        connectionError: null,
        snapshotRequested: false,
        
        // SECURITY: User context for data isolation
        currentUserId: null,
        
        // 🚀 NEW: Cache hydration method
        initializeFromCache: (cachedData: LiveTrade[]) => {
          set((state) => {
            console.log('💾 [Position Store] Initializing from React Query cache:', cachedData.length, 'positions');
            
            // Clear existing data
            state.positions.clear();
            
            // Add cached positions
            cachedData.forEach((position) => {
              const key = getPositionKey(position.accountname, position.symbol);
              state.positions.set(key, position);
            });
            
            // Mark as initialized with cache data
            state.isInitialized = true;
            state.isLoading = false;
            state.error = null;
            state.lastSnapshotTime = Date.now();
            
            updateMetadata(state);
            console.log('✅ Position store hydrated from cache:', state.positions.size, 'positions, Total PnL:', state.totalUnrealizedPnl);
          });
        },      // SSE Event Handlers
      handlePositionSnapshot: (event: PositionSnapshotEvent) => {
        set((state) => {
          console.log('📸 Position Snapshot received:', event.data.positions.length, 'positions');
          
          // Smart merge: Keep existing objects if data hasn't changed
          const newPositions = new Map<string, LiveTrade>();
          const existingPositions = state.positions;
          
          // Process incoming positions
          event.data.positions.forEach((position) => {
            const key = getPositionKey(position.accountname, position.symbol);
            const existing = existingPositions.get(key);
            
            // Check if position data has actually changed
            if (existing && positionsAreEqual(existing, position)) {
              // Data hasn't changed, keep the existing object reference
              newPositions.set(key, existing);
              console.log('♻️ Preserved existing position object:', key);
            } else {
              // Data has changed or is new, use the new position
              newPositions.set(key, position);
              if (existing) {
                console.log('🔄 Updated position data:', key);
              } else {
                console.log('🆕 Added new position:', key);
              }
            }
          });
          
          // Check for removed positions
          for (const [key] of existingPositions) {
            if (!newPositions.has(key)) {
              console.log('❌ Position removed:', key);
            }
          }
          
          // Replace the positions map
          state.positions = newPositions;
          
          // Update state
          state.isInitialized = true;
          state.isLoading = false;
          state.error = null;
          state.lastSnapshotTime = event.data.timestamp;
          state.snapshotRequested = false;
          
          updateMetadata(state);
          
          console.log('✅ Position snapshot applied (smart merge):', state.positions.size, 'positions, Total PnL:', state.totalUnrealizedPnl);
        });
      },
      
      handlePositionOpened: (event: PositionOpenedEvent) => {
        set((state) => {
          const key = getPositionKey(event.data.account, event.data.symbol);
          console.log('🟢 Position opened event received:', key, 'PnL:', event.data.pnl);
          console.log('🔍 Event data structure:', {
            hasPosition: !!event.data.position,
            eventDataKeys: Object.keys(event.data),
            positionKeys: event.data.position ? Object.keys(event.data.position) : 'N/A'
          });
          
          if (event.data.position) {
            state.positions.set(key, event.data.position);
            updateMetadata(state);
            console.log('✅ Position added to store:', key, 'Total positions:', state.positions.size);
          } else {
            console.warn('⚠️ Position opened event missing position data:', {
              event: event,
              availableData: event.data,
              expectedPosition: 'event.data.position',
              currentPositions: state.positions.size
            });
            
            // Try to create a minimal position object from available data
            if (event.data.pnl !== undefined && event.data.qty !== undefined) {
              console.log('🔧 Attempting to create minimal position from available data...');
              const minimalPosition = {
                seq: 0,
                symbol: event.data.symbol,
                side: event.data.side || 'Unknown',
                strategyname: undefined,
                accountname: event.data.account,
                unrealisedPnl: String(event.data.pnl),
                avgPrice: '0',
                markPrice: String(event.data.mark_price || 0),
                takeProfit: '',
                stopLoss: '',
                createdTime: String(Date.now()),
                size: String(event.data.qty),
                positionValue: '0',
                leverage: '1',
              } as LiveTrade;
              
              state.positions.set(key, minimalPosition);
              updateMetadata(state);
              console.log('🔧 Minimal position created:', key, 'Total positions:', state.positions.size);
            } else {
              console.error('❌ Cannot create position - insufficient data:', event.data);
            }
          }
        });
      },
      
      handlePositionClosed: (event: PositionClosedEvent) => {
        set((state) => {
          const key = getPositionKey(event.data.account, event.data.symbol);
          console.log('🔴 Position closed:', key, 'Final PnL:', event.data.final_pnl);
          
          const existed = state.positions.has(key);
          state.positions.delete(key);
          updateMetadata(state);
          
          if (existed) {
            console.log('✅ Position removed from store:', key);
          } else {
            console.warn('⚠️ Attempted to close non-existent position:', key);
          }
        });
      },
      
      handlePositionPnlChange: (event: PositionPnlChangeEvent) => {
        set((state) => {
          const key = getPositionKey(event.data.account, event.data.symbol);
          const position = state.positions.get(key);
          
          if (position) {
            // Check if PnL actually changed to avoid unnecessary updates
            const currentPnl = position.unrealisedPnl;
            const newPnl = event.data.new_pnl?.toString();
            const currentMarkPrice = position.markPrice;
            const newMarkPrice = event.data.mark_price?.toString();
            
            if (currentPnl !== newPnl || currentMarkPrice !== newMarkPrice) {
              // Create a new position object with updated data
              const updatedPosition = {
                ...position,
                unrealisedPnl: newPnl || position.unrealisedPnl,
                markPrice: newMarkPrice || position.markPrice,
              };
              
              state.positions.set(key, updatedPosition);
              updateMetadata(state);
              
              console.log('💰 Position PnL updated:', key, position.unrealisedPnl, '→', updatedPosition.unrealisedPnl);
            } else {
              console.log('💰 Position PnL unchanged, skipping update:', key, 'Current PnL:', position.unrealisedPnl);
            }
          } else {
            console.warn('⚠️ PnL change for unknown position:', key, 'New PnL:', event.data.new_pnl);
            // Position might be missing from cache - will be handled by snapshot request if needed
          }
        });
      },
      
      handlePositionModified: (event: PositionModifiedEvent) => {
        set((state) => {
          const key = getPositionKey(event.data.account, event.data.symbol);
          const position = state.positions.get(key);
          
          if (position) {
            // Create a new position object with updated data
            const updatedPosition = {
              ...position,
              size: event.data.new_qty.toString(),
              unrealisedPnl: event.data.pnl.toString(),
              markPrice: event.data.mark_price.toString(),
            };
            
            state.positions.set(key, updatedPosition);
            updateMetadata(state);
            
            console.log('🔄 Position modified:', key, 'Qty:', event.data.old_qty, '→', event.data.new_qty);
          } else {
            console.warn('⚠️ Modification for unknown position:', key);
          }
        });
      },
      
      handlePositionMilestone: (event: PositionMilestoneEvent) => {
        set((state) => {
          const key = getPositionKey(event.data.account, event.data.symbol);
          console.log('🎯 Position milestone:', key, event.data.milestone_percentage + '%', event.data.milestone_direction);
          
          // Milestone events don't modify position data, just provide notifications
          // The position data should already be updated by PnL change events
          updateMetadata(state);
        });
      },
      
      handlePositionsRefresh: (event: PositionsRefreshEvent) => {
        set((state) => {
          console.log('🔄 Positions refresh requested:', event.data.reason);
          
          // A refresh event indicates we should request a new snapshot
          state.snapshotRequested = true;
          updateMetadata(state);
        });
      },
      
      // State management
      requestPositionSnapshot: () => {
        set((state) => {
          if (!state.snapshotRequested) {
            console.log('📸 [Position Store] Snapshot request - updating state only');
            state.snapshotRequested = true;
            state.isLoading = true;
            state.error = null;
          }
        });
        
        // Note: We don't dispatch the custom event here to avoid infinite loops
        // The actual HTTP request is handled by usePositionSSE
      },
      
      clearPositions: () => {
        set((state) => {
          console.log('🧹 Clearing all positions from store');
          state.positions.clear();
          state.isInitialized = false;
          state.isLoading = false;
          state.error = null;
          state.lastSnapshotTime = null;
          state.snapshotRequested = false;
          updateMetadata(state);
        });
      },
      
      resetError: () => {
        set((state) => {
          state.error = null;
        });
      },
      
      setLoading: (loading: boolean) => {
        set((state) => {
          state.isLoading = loading;
        });
      },
      
      setConnectionStatus: (connected: boolean, error?: string) => {
        set((state) => {
          state.isConnected = connected;
          state.connectionError = error || null;
          
          if (connected && !state.isInitialized && !state.snapshotRequested) {
            // Auto-request snapshot on connection
            state.snapshotRequested = true;
            state.isLoading = true;
          }
        });
      },
      
      // Selectors
      getPosition: (account: string, symbol: string) => {
        const key = getPositionKey(account, symbol);
        return get().positions.get(key);
      },
      
      getPositionsByAccount: (account: string) => {
        const positions: LiveTrade[] = [];
        for (const [, position] of get().positions.entries()) {
          if (position.accountname === account) {
            positions.push(position);
          }
        }
        return positions;
      },
      
      hasOpenPositions: () => {
        return get().positions.size > 0;
      },
      
      getPositionsBySymbol: (symbol: string) => {
        const positions: LiveTrade[] = [];
        for (const [, position] of get().positions.entries()) {
          if (position.symbol === symbol) {
            positions.push(position);
          }
        }
        return positions;
      },
      
      getTotalPnl: () => {
        return get().totalUnrealizedPnl;
      },
      
      // SECURITY: User context management methods
      setUserContext: (userId: string | null) => {
        set((state) => {
          const previousUserId = state.currentUserId;
          
          // Only log if user context actually changes
          if (previousUserId !== userId) {
            const prevLabel = previousUserId ? `${previousUserId.substring(0, 20)}...` : 'anonymous';
            const newLabel = userId ? `${userId.substring(0, 20)}...` : 'anonymous';
            console.log(`🔐 [Position Store] User transition: ${prevLabel} → ${newLabel}`);
            
            // Update storage user ID FIRST before changing state
            setPositionStoreUserId(userId);
            
            state.currentUserId = userId;
            
            // SECURITY: Clear positions if switching between different users
            if (previousUserId && userId && previousUserId !== userId) {
              console.log(`🧹 [Position Store] Clearing stale position data for previous user`);
              state.positions.clear();
              state.isInitialized = false;
              state.totalPositions = 0;
              state.totalUnrealizedPnl = 0;
              state.lastSnapshotTime = null;
            }
          }
        });
      },
      
      clearUserData: () => {
        set((state) => {
          console.log('🧹 [Position Store] Clearing all user data and context');
          state.positions.clear();
          state.isInitialized = false;
          state.isLoading = false;
          state.error = null;
          state.lastSnapshotTime = null;
          state.snapshotRequested = false;
          state.currentUserId = null;
          updateMetadata(state);
        });
      },
    })),
    {
      name: 'position-store-devtools',
      // Only serialize basic state for debugging, not the Map
      serialize: {
        options: {
          map: false,
        },
      },
    }
  ),
  {
    name: 'position-store-persist', // Name parameter (not used with custom storage)
    
    // SECURITY: Use custom user-aware storage
    storage: createJSONStorage(() => userAwareStorage),
    
    // Only persist essential data, skip transient states
    partialize: (state) => ({
      positions: Array.from(state.positions.entries()), // Convert Map to Array for persistence
      isInitialized: state.isInitialized,
      totalPositions: state.totalPositions,
      totalUnrealizedPnl: state.totalUnrealizedPnl,
      lastUpdate: state.lastUpdate,
      lastSnapshotTime: state.lastSnapshotTime,
      currentUserId: state.currentUserId // SECURITY: Persist user context for validation
    }),
    
    // Handle rehydration from localStorage with security validation
    onRehydrateStorage: () => (state) => {
      if (state) {
        const persistedUserId = (state as any).currentUserId;
        const positionsArray = (state as any).positions || [];
        
        // SECURITY: Validate user context on rehydration
        if (persistedUserId && currentStorageUserId && persistedUserId !== currentStorageUserId) {
          console.log(`🔒 [Position Store] Clearing stale user data on rehydration`);
          console.log(`   Previous: ${persistedUserId.substring(0, 20)}..., Current: ${currentStorageUserId.substring(0, 20)}...`);
          
          // Clear the stale data (expected during user transitions)
          state.positions = new Map();
          state.currentUserId = currentStorageUserId;
          state.isInitialized = false;
          state.totalPositions = 0;
          state.totalUnrealizedPnl = 0;
          state.lastSnapshotTime = null;
          return;
        }
        
        // SECURITY: If no currentStorageUserId yet, don't load any data
        // This prevents loading data before user context is established
        if (!currentStorageUserId && positionsArray.length > 0) {
          console.log('🔒 [Position Store] Skipping rehydration - user context not yet established');
          state.positions = new Map();
          state.currentUserId = null;
          state.isInitialized = false;
          state.totalPositions = 0;
          state.totalUnrealizedPnl = 0;
          state.lastSnapshotTime = null;
          return;
        }
        
        // User context matches or no positions to load - safe to rehydrate
        state.positions = new Map(positionsArray);
        state.currentUserId = persistedUserId;
        
        console.log('💾 [Position Store] Rehydrated from localStorage:', {
          positions: state.positions.size,
          persistedUserId: persistedUserId?.substring(0, 20) + '...' || 'none',
          currentUserId: currentStorageUserId?.substring(0, 20) + '...' || 'none',
          validated: true
        });
      }
    },
    
    // Version for cache invalidation when structure changes
    version: 2, // Bump version to invalidate old data
    
    // SECURITY: Skip automatic hydration - wait for user context to be set
    skipHydration: false // We'll handle validation in onRehydrateStorage instead
  }
  )
);

// Convenience hooks for React components
export const usePositions = () => {
  return usePositionStore((state) => Array.from(state.positions.values()));
};

export const useHasOpenPositions = () => {
  return usePositionStore((state) => state.hasOpenPositions());
};

export const usePositionBySymbol = (account: string, symbol: string) => {
  return usePositionStore((state) => state.getPosition(account, symbol));
};

export const usePositionLoadingState = () => {
  return usePositionStore((state) => ({
    isLoading: state.isLoading,
    isInitialized: state.isInitialized,
    error: state.error,
  }));
};

export const usePositionMetadata = () => {
  return usePositionStore((state) => ({
    totalPositions: state.totalPositions,
    totalPnl: state.totalUnrealizedPnl,
    lastUpdate: state.lastUpdate,
    lastSnapshotTime: state.lastSnapshotTime,
  }));
};

export const usePositionConnectionState = () => {
  return usePositionStore((state) => ({
    isConnected: state.isConnected,
    connectionError: state.connectionError,
    snapshotRequested: state.snapshotRequested,
  }));
};